import * as THREE from 'three';
import type { FollowOhtCameraMode } from '../types';
import { DEFAULT_UNITS_PER_METER_WITHOUT_FLOORPLAN } from '../constants';
import { OHT_MODEL_SCALE_MULTIPLIER } from './layout3d';

export type { FollowOhtCameraMode };

/** Rail beam half-height relative to the rail group (matches DualRail / gantry). */
export const RAIL_HALF_HEIGHT = 1.5;

export type OhtCameraKind = 'onboard' | 'follow';

export interface OhtWorldPose {
  x: number;
  y: number;
  z: number;
  headingDeg: number;
  meshDepth: number;
  bodyLength: number;
  scale: number;
}

export interface OhtCameraPose {
  position: [number, number, number];
  target: [number, number, number];
  fov: number;
}

/** Layout world units per meter at default scale (matches FAB track spacing). */
export function worldUnitsPerMeter(scale = 1): number {
  return DEFAULT_UNITS_PER_METER_WITHOUT_FLOORPLAN * (scale > 0 ? scale : 1);
}

function metersInWorld(meters: number, scale: number): number {
  return meters * worldUnitsPerMeter(scale);
}

/** Underside of the rail beam — OHTs hang from this lip. */
export function getTrackBottomY(railGroupY: number, scale = 1): number {
  return railGroupY - RAIL_HALF_HEIGHT * scale;
}

/** Shortest-path interpolation for OHT heading (degrees). */
export function lerpAngleDeg(fromDeg: number, toDeg: number, t: number): number {
  const u = Math.min(1, Math.max(0, t));
  const delta = ((toDeg - fromDeg + 540) % 360) - 180;
  return fromDeg + delta * u;
}

function followThirdPersonPose(
  pose: OhtWorldPose,
  mode: FollowOhtCameraMode
): OhtCameraPose {
  const s = pose.scale > 0 ? pose.scale : 1;
  const rad = (pose.headingDeg * Math.PI) / 180;
  const fx = Math.cos(rad);
  const fz = Math.sin(rad);
  const rightX = fz;
  const rightZ = -fx;

  const hang = Math.max(pose.meshDepth, 1) * OHT_MODEL_SCALE_MULTIPLIER * s;
  const trackBottomY = getTrackBottomY(pose.y, s);
  const bodyMidY = trackBottomY - hang * 0.5;
  const body = Math.max(pose.bodyLength, 8) * s;

  const minFollow = metersInWorld(8, s);
  const maxFollow = metersInWorld(15, s);

  let followDist: number;
  let sideOff: number;
  let lookAhead: number;
  let camY: number;
  let lookY: number;
  let fov: number;

  switch (mode) {
    case 'side': {
      // Technical side/elevation view of the complete vertical fab layout:
      // - Shows the complete OHT+ on top of the track without cropping
      // - Simultaneously frames the area below the track down to ground level (lifter & equipment)
      // - Targets the vertical mid-elevation of the fab envelope with wide technical framing
      const ohtTopY = pose.y + RAIL_HALF_HEIGHT * s + hang * 0.6;
      const verticalFabMidY = (ohtTopY + 0) * 0.5;
      const verticalFabSpan = Math.max(ohtTopY, body * 1.5, 30 * s);

      fov = 50;
      // Generous distance to comfortably frame the complete vertical structure with clean margins:
      const targetFrustumHeight = verticalFabSpan * 1.48;
      const elevationDist = targetFrustumHeight / (2 * Math.tan((fov * Math.PI) / 360));

      sideOff = THREE.MathUtils.clamp(
        elevationDist,
        metersInWorld(20, s),
        metersInWorld(38, s)
      );
      // Clean technical side elevation with subtle axonometric angle for 3D legibility
      followDist = THREE.MathUtils.clamp(body * 0.25, metersInWorld(1.2, s), metersInWorld(3.5, s));
      lookAhead = 0;
      camY = pose.y + hang * 0.55;
      lookY = verticalFabMidY;
      break;
    }
    case 'elevated':
      followDist = THREE.MathUtils.clamp(body * 3.6, minFollow * 1.1, maxFollow * 1.4);
      sideOff = THREE.MathUtils.clamp(body * 1.2, metersInWorld(3.5, s), metersInWorld(7.5, s));
      lookAhead = THREE.MathUtils.clamp(body * 0.5, metersInWorld(3, s), metersInWorld(6, s));
      camY = trackBottomY + hang * 2.2;
      lookY = bodyMidY;
      fov = 54;
      break;
    case 'chase':
    default:
      followDist = THREE.MathUtils.clamp(body * 3.2, minFollow, maxFollow * 1.2);
      sideOff = THREE.MathUtils.clamp(body * 0.55, metersInWorld(2.5, s), metersInWorld(5, s));
      lookAhead = THREE.MathUtils.clamp(body * 0.4, metersInWorld(2.5, s), metersInWorld(6, s));
      camY = trackBottomY + hang * 0.45;
      lookY = bodyMidY;
      fov = 50;
      break;
  }

  return {
    position: [
      pose.x - fx * followDist + rightX * sideOff,
      camY,
      pose.z - fz * followDist + rightZ * sideOff,
    ],
    target: [pose.x + fx * lookAhead, lookY, pose.z + fz * lookAhead],
    fov,
  };
}

/**
 * Onboard sits at the bottom of the track looking forward so the hanging body
 * fills the lower half of the frame. Follow uses third-person chase / side / elevated modes.
 */
export function getOhtCameraPose(
  kind: OhtCameraKind,
  pose: OhtWorldPose,
  followMode: FollowOhtCameraMode = 'chase'
): OhtCameraPose {
  const s = pose.scale > 0 ? pose.scale : 1;
  const rad = (pose.headingDeg * Math.PI) / 180;
  const fx = Math.cos(rad);
  const fz = Math.sin(rad);
  const hang = Math.max(pose.meshDepth, 1) * OHT_MODEL_SCALE_MULTIPLIER * s;
  const trackBottomY = getTrackBottomY(pose.y, s);
  const bodyMidY = trackBottomY - hang * 0.5;
  const body = Math.max(pose.bodyLength, 8) * s;

  const rightX = fz;
  const rightZ = -fx;

  if (kind === 'onboard') {
    const along = body * 0.06;
    const side = Math.max(5 * s, body * 0.38);
    const lookAhead = Math.max(48 * s, body * 2.2);
    return {
      position: [
        pose.x + fx * along + rightX * side,
        trackBottomY,
        pose.z + fz * along + rightZ * side,
      ],
      target: [pose.x + fx * lookAhead, bodyMidY, pose.z + fz * lookAhead],
      fov: 62,
    };
  }

  return followThirdPersonPose(pose, followMode);
}
